import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import api from "../api";

const statusVariant = (status) => {
  if (status === "active") return "success";
  if (status === "canceled") return "danger";
  if (status === "past_due") return "warning";
  return "secondary";
};

const SummaryCard = ({ label, value, sub }) => (
  <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "10px" }}>
    <Card.Body className="py-3">
      <p className="text-muted mb-1" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </p>
      <p className="mb-0 fw-bold" style={{ fontSize: "1.6rem", lineHeight: 1.2 }}>{value}</p>
      {sub && <p className="mb-0 text-muted" style={{ fontSize: "0.8rem" }}>{sub}</p>}
    </Card.Body>
  </Card>
);

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [disk, setDisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get("/admin/dashboard", { withCredentials: true })
      .then((res) => {
        setUsers(res.data.users);
        setDisk(res.data.disk);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          setError("Access denied. You are not authorized to view this page.");
        } else {
          setError("Failed to load dashboard data.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => ({
    users: users.length,
    events: users.reduce((sum, e) => sum + e.usage.total_events, 0),
    memoryGb: (Math.round(users.reduce((sum, e) => sum + e.usage.memory_used_gb, 0) * 10) / 10).toFixed(1),
  }), [users]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return users;
    return users.filter((e) =>
      e.user.email.toLowerCase().includes(q) ||
      e.user.firstname.toLowerCase().includes(q) ||
      e.user.lastname.toLowerCase().includes(q)
    );
  }, [users, search]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="secondary" />
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Summary cards */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <SummaryCard label="Total Users" value={totals.users} />
        </Col>
        <Col xs={6} md={3}>
          <SummaryCard label="Total Events" value={totals.events} />
        </Col>
        <Col xs={6} md={3}>
          <SummaryCard label="App Storage" value={`${totals.memoryGb} GB`} sub="across all users" />
        </Col>
        <Col xs={6} md={3}>
          {disk ? (
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "10px" }}>
              <Card.Body className="py-3">
                <p className="text-muted mb-1" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Disk
                </p>
                <p className="mb-1 fw-bold" style={{ fontSize: "1.6rem", lineHeight: 1.2 }}>
                  {disk.used_percent}%
                </p>
                <div style={{ background: "#e9ecef", borderRadius: "4px", height: "6px", marginBottom: "6px" }}>
                  <div
                    style={{
                      width: `${disk.used_percent}%`,
                      height: "100%",
                      borderRadius: "4px",
                      background: disk.used_percent > 85 ? "#dc3545" : disk.used_percent > 60 ? "#fd7e14" : "#198754",
                    }}
                  />
                </div>
                <p className="mb-0 text-muted" style={{ fontSize: "0.8rem" }}>
                  {disk.used_gb} / {disk.total_gb} GB &mdash; {disk.free_gb} GB free
                </p>
              </Card.Body>
            </Card>
          ) : (
            <SummaryCard label="Disk" value="—" />
          )}
        </Col>
      </Row>

      {/* Header + search */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-3">
        <h5 className="mb-0 fw-semibold" style={{ color: "#343a40", whiteSpace: "nowrap" }}>
          Users
        </h5>
        <Form.Control
          type="search"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: "300px", fontSize: "0.88rem" }}
        />
      </div>

      {/* User rows */}
      {filtered.map((entry) => (
        <div
          key={entry.user.user_id}
          className="mb-3 p-3"
          style={{ background: "#f8f9fa", borderRadius: "12px", border: "1px solid #e9ecef" }}
        >
          <p className="mb-2 text-muted" style={{ fontSize: "0.78rem", letterSpacing: "0.4px", textTransform: "uppercase", fontWeight: 600 }}>
            #{entry.user.user_id} &mdash; {entry.user.email}
          </p>
          <Row className="g-3">
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: "10px" }}>
                <Card.Body>
                  <p className="text-muted mb-2" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    User Info
                  </p>
                  <p className="mb-1 fw-semibold" style={{ fontSize: "1rem" }}>
                    {entry.user.firstname} {entry.user.lastname}
                  </p>
                  <p className="mb-1 text-muted" style={{ fontSize: "0.85rem" }}>{entry.user.email}</p>
                  {entry.user.created_date && (
                    <p className="mb-0 text-muted" style={{ fontSize: "0.8rem" }}>
                      Joined {new Date(entry.user.created_date).toLocaleDateString()}
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: "10px" }}>
                <Card.Body>
                  <p className="text-muted mb-2" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Subscription
                  </p>
                  {entry.subscription ? (
                    <>
                      <p className="mb-1 fw-semibold" style={{ fontSize: "1rem" }}>{entry.subscription.plan_name}</p>
                      <p className="mb-2 text-muted" style={{ fontSize: "0.85rem" }}>{entry.subscription.billing_cycle}</p>
                      <Badge bg={statusVariant(entry.subscription.status)} className="text-capitalize">
                        {entry.subscription.status}
                      </Badge>
                    </>
                  ) : (
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>No active subscription</p>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: "10px" }}>
                <Card.Body>
                  <p className="text-muted mb-2" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Usage
                  </p>
                  <p className="mb-2" style={{ fontSize: "1rem" }}>
                    <span className="fw-semibold">{entry.usage.total_events}</span>
                    <span className="text-muted ms-1" style={{ fontSize: "0.85rem" }}>events</span>
                  </p>
                  {(() => {
                    const used = Math.round(entry.usage.memory_used_gb * 10) / 10;
                    const limit = entry.subscription?.memory_limit_gb ?? null;
                    const pct = limit ? Math.min((used / limit) * 100, 100) : null;
                    const color = pct == null ? "#6c757d" : pct > 85 ? "#dc3545" : pct > 60 ? "#fd7e14" : "#198754";
                    return (
                      <>
                        <p className="mb-1" style={{ fontSize: "0.95rem" }}>
                          <span className="fw-semibold" style={{ color }}>{used} GB</span>
                          {limit != null && (
                            <span className="text-muted" style={{ fontSize: "0.85rem" }}> / {limit} GB</span>
                          )}
                        </p>
                        {pct != null && (
                          <div style={{ background: "#e9ecef", borderRadius: "4px", height: "5px" }}>
                            <div style={{ width: `${pct}%`, height: "100%", borderRadius: "4px", background: color, transition: "width 0.3s" }} />
                          </div>
                        )}
                      </>
                    );
                  })()}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      ))}

      {filtered.length === 0 && (
        <p className="text-muted text-center py-5">
          {search ? "No users match your search." : "No users found."}
        </p>
      )}
    </Container>
  );
};

export default AdminDashboard;
